import React from 'react'
import { observer } from 'mobx-react-lite'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfToday } from 'date-fns/endOfToday'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import {
  ANALYTICS_EVENTS,
  Loader,
  MicroWidgetContainer,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useRecurringTransactionsStore,
  useTransactionStore,
  useWidgetLoadTimer,
} from '../common'

import { ZeroStateMicroWidget } from './ZeroStateMicroWidget'
import { MicroWidgetContent } from './components/MicroWidgetContent'

interface RecurringTransactionsMicroWidgetProps {
  onCTAClick: () => void
}

export const RecurringTransactionsMicroWidget = observer(
  ({ onCTAClick }: RecurringTransactionsMicroWidgetProps) => {
    const { onEvent } = useEvent()

    const { isInitialized, selectedAccountGuids } = useGlobalUiStore()
    const {
      loadRepeatingTransactionData,
      upcomingRecurrences,
      repeatingTransactions,
      setDateRange,
      isDataLoaded,
    } = useRecurringTransactionsStore()
    const { setFilter } = useTransactionStore()
    const { recurring: copy, budgets: budgetsCopy } = useGlobalCopyStore()

    useWidgetLoadTimer({
      widgetName: 'RecurringTransactionsMicroWidget',
      isLoaded: isDataLoaded,
    })

    React.useEffect(() => {
      if (!isDataLoaded) {
        // Load the repeating transactions and related data
        loadRepeatingTransactionData().finally(() => {
          onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_LOAD_WIDGET, {
            state: upcomingRecurrences.length > 0 ? 'default' : 'zeroState',
          })
        })
      }
    }, [])

    React.useEffect(() => {
      if (isInitialized) {
        setFilter({
          dateRange: { start: subDays(startOfToday(), 90), end: endOfToday() },
          accounts: selectedAccountGuids,
        })
        setDateRange({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) })
      }
    }, [isInitialized, selectedAccountGuids])

    const handleCTAClick = () => {
      onEvent(
        upcomingRecurrences.length > 0
          ? ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_GET_STARTED
          : ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_VIEW_ALL,
      )
      onCTAClick()
    }

    if (!isInitialized || !isDataLoaded) return <Loader height={100} />

    const showZeroState = !repeatingTransactions?.length

    return (
      <MicroWidgetContainer
        className="mx-recurring-transaction-microwidget"
        ctaLabel={showZeroState ? budgetsCopy.get_started_cta : copy.primary_cta}
        onCTAClick={handleCTAClick}
        title={copy.recurring_expenses}
      >
        {!showZeroState ? <MicroWidgetContent /> : <ZeroStateMicroWidget />}
      </MicroWidgetContainer>
    )
  },
)
